import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { actions, rainModalSelector } from "reducers/rainModal";
import { actions as notificationActions } from "reducers/notification";
import { regionSelector } from "reducers/regions";
import {
  RainCreationParamsType,
  RainDropDownState,
  RainsRequestParamsType,
  RainTimeFilter,
  ToggleOrderType,
} from "reducers/types/rainModal";
import { AppDispatch, RootState } from "store";
import { DATE_FORMATS, OPPOSE_RAIN_TYPES, RAIN_TYPES } from "utils/constants";
import { isDateBeforeToday, isToday, isTodayTimeBeforeNow } from "utils/date";
import { FormRefType } from "utils/types";
import { RainBonusModal } from "./RainBonusModal";
import {
  RainBonusModalContainerPropsType,
  RainInputsValuesType,
} from "./types";
import { NotificationStateType } from "reducers/types/notification";
import { dirtyAlert } from "utils/alerts";
import { diferenceInMinutes } from "utils/validations";

const RainBonusModalContainer = (
  props: RainBonusModalContainerPropsType & ConnectorProps
): JSX.Element => {
  const MIN_DELTA = 60;
  const MINUTS_INTERVAL = 15;
  const { isOpen, rainDirty, dirtyCreation, showNotification } = props;
  const [resolutionHeightModal, setresolutionHeightModal] = useState(550);
  const [isDisabledSearch, setIsDisabledSearch] = useState(false);
  const formRef: FormRefType<RainInputsValuesType> = useRef(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dirtyRainRef = useRef<HTMLDivElement>(null);
  const creationEmptyValues: RainInputsValuesType = useMemo(
    () => ({
      rainDate: undefined,
      rainTime: {
        start: undefined,
        end: undefined,
      },
    }),
    []
  );
  const [creationInitialValues, setCreationInitialValues] =
    useState<RainInputsValuesType>(creationEmptyValues);

  const initModal = useCallback(() => {
    props.setFilter({
      date: undefined,
      finishAt: undefined,
      startAt: undefined,
    });
    props.setDropdownState("closed");
    props.setOrder({ tab: "next", newOrder: "ASC" });
    props.setOrder({ tab: "previous", newOrder: "DESC" });
    props.resetValidationError();
    props.resetBlockedTab();
    if (formRef.current) formRef.current.restart(creationEmptyValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (window.screen.availHeight < 760) {
        setresolutionHeightModal(550);
      }
      if (window.screen.availHeight > 900) {
        setresolutionHeightModal(675);
      }
    } else {
      initModal();
    }

    return () => {
      initModal();
    };
  }, [creationEmptyValues, initModal, isOpen]);

  const toggleDropdown = useCallback(
    (
      tab: RainDropDownState,
      e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      e?.stopPropagation();
      if (rainDirty) return;
      if (props.blockedTab[tab]) return;
      if (props.dropdownState === tab) {
        props.setDropdownState("closed");
      } else {
        props.setDropdownState(tab);
        props.getRains(
          {
            date: props.filter.date,
            finishAt: props.filter.finishAt,
            startAt: props.filter.startAt,
            type: RAIN_TYPES[tab],
            order: props.order[tab],
          },
          props.actualRegion?.id || ""
        );
        props.setBlockedTab(OPPOSE_RAIN_TYPES[tab]);
      }
    },
    [rainDirty, props]
  );

  const toggleOrder = useCallback(
    (
      tab: RainDropDownState,
      e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      e?.stopPropagation();
      const newOrder = props.order[tab] === "ASC" ? "DESC" : "ASC";
      props.setOrder({ tab, newOrder });
      props.getRains(
        {
          type: RAIN_TYPES[tab],
          order: newOrder,
          date: props.filter.date,
          finishAt: props.filter.finishAt,
          startAt: props.filter.startAt,
        },
        props.actualRegion?.id || ""
      );
    },
    [props]
  );

  const dispatchSearchActions = useCallback(
    (
      tab: RainDropDownState,
      dateFilter?: string,
      initial?: string,
      end?: string
    ) => {
      const timeFilter = {
        finishAt: end && `${end}:00`,
        startAt: initial && `${initial}:00`,
      };
      props.setDropdownState(tab);
      props.setFilter({ date: dateFilter, ...timeFilter });
      props.getRains(
        {
          date: dateFilter,
          order: props.order[tab],
          ...timeFilter,
          type: RAIN_TYPES[tab],
        },
        props.actualRegion?.id || ""
      );
    },
    [props]
  );

  const searchRainsWhenCreate = useCallback(
    (date: string, finishAt?: string) => {
      let tab: RainDropDownState =
        props.dropdownState !== "closed" ? props.dropdownState : "next";
      let dateFilter = undefined;
      if (date) {
        if (finishAt && isToday(date))
          tab = isTodayTimeBeforeNow(date, finishAt) ? "previous" : "next";
        else {
          tab = isDateBeforeToday(date) ? "previous" : "next";
        }
        dateFilter = moment(date, DATE_FORMATS.shortDate).format(
          DATE_FORMATS.shortISODate
        );
        props.setBlockedTab(OPPOSE_RAIN_TYPES[tab]);
      } else props.resetBlockedTab();
      dispatchSearchActions(tab, dateFilter);
    },
    [dispatchSearchActions, props]
  );

  const searchRains = useCallback(
    (values: RainInputsValuesType) => {
      let tab: RainDropDownState =
        props.dropdownState !== "closed" ? props.dropdownState : "next";
      let dateFilter = undefined;
      const { initial, end } = values.rainTime;
      const date = values.rainDate;
      if (date) {
        let blockTab = true;
        if (isToday(date)) {
          let changeTabTo: RainDropDownState | undefined = undefined;
          if (initial || end) {
            if (initial && end) {
              if (isTodayTimeBeforeNow(date, end)) changeTabTo = "previous";
              if (!isTodayTimeBeforeNow(date, initial)) changeTabTo = "next";
            } else {
              if (!initial && isTodayTimeBeforeNow(date, end!))
                changeTabTo = "previous";
              if (!end && !isTodayTimeBeforeNow(date, initial!))
                changeTabTo = "next";
            }
          }
          blockTab = !!changeTabTo;
          tab = changeTabTo ? changeTabTo : tab;
        } else {
          tab = isDateBeforeToday(date) ? "previous" : "next";
        }

        dateFilter = moment(date, DATE_FORMATS.shortDate).format(
          DATE_FORMATS.shortISODate
        );

        blockTab
          ? props.setBlockedTab(OPPOSE_RAIN_TYPES[tab])
          : props.resetBlockedTab();
      } else props.resetBlockedTab();
      dispatchSearchActions(tab, dateFilter, initial, end);
    },
    [dispatchSearchActions, props]
  );

  const checkInterval = (value: string) => {
    const [, minute] = value.split(":");
    const m = Number(minute);
    if (!minute || isNaN(m)) return true;
    return m % MINUTS_INTERVAL === 0;
  };

  const disabledSearch = useCallback((values: RainInputsValuesType) => {
    setIsDisabledSearch(false);
    const { initial, end } = values.rainTime;
    if (initial && end) {
      const deltaMin = diferenceInMinutes(initial, end);
      if (!!deltaMin && deltaMin < MIN_DELTA) {
        setIsDisabledSearch(true);
        return;
      }
    }
    setIsDisabledSearch(
      (!!initial && !checkInterval(initial)) || (!!end && !checkInterval(end))
    );
  }, []);

  const scroll = useCallback(() => {
    if (dirtyRainRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scroll({
        top: dirtyRainRef.current.offsetTop,
        left: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const handleSearch = useCallback(
    (
      values: RainInputsValuesType,
      e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      e?.stopPropagation();
      if (rainDirty && showNotification) {
        showNotification(
          dirtyAlert({
            scroll: true,
            externalCancel: true,
            scrollTo: rainDirty ? scroll : undefined,
            onCancel: () => {
              props.setRainDirty(false);
              searchRains(values);
            },
          })
        );
      } else searchRains(values);
    },
    [props, rainDirty, scroll, searchRains, showNotification]
  );

  const onCreationSuccess = useCallback(
    (date: string, finishAt: string) => {
      setCreationInitialValues({
        rainDate: date,
        rainTime: {
          initial: undefined,
          end: undefined,
        },
      });
      props.setDirtyCreation(false);
      searchRainsWhenCreate(date, finishAt);
      if (formRef.current) formRef.current.restart();
    },
    [props, searchRainsWhenCreate]
  );

  const createRain = useCallback(
    (values: RainInputsValuesType) => {
      if (rainDirty && showNotification) {
        showNotification(
          dirtyAlert({
            scroll: true,
            externalCancel: true,
            scrollTo: rainDirty ? scroll : undefined,
            onCancel: () =>
              props.addRain({
                requestParams: {
                  date: values.rainDate || "",
                  startAt: values.rainTime.initial || "",
                  finishAt: values.rainTime.end || "",
                  regionId: props.actualRegion?.id || "",
                },
                callback: onCreationSuccess,
              }),
          })
        );
      } else
        props.addRain({
          requestParams: {
            date: values.rainDate || "",
            startAt: values.rainTime.initial || "",
            finishAt: values.rainTime.end || "",
            regionId: props.actualRegion?.id || "",
          },
          callback: onCreationSuccess,
        });
    },
    [rainDirty, showNotification, props, onCreationSuccess, scroll]
  );

  const handleClose = useCallback(() => {
    if ((rainDirty || dirtyCreation) && showNotification) {
      showNotification(
        dirtyAlert({
          scroll: rainDirty,
          externalCancel: true,
          scrollTo: rainDirty ? scroll : undefined,
          onCancel: () => {
            props.onClose();
            props.setRainDirty(false);
            props.setDirtyCreation(false);
          },
        })
      );
    } else props.onClose();
  }, [rainDirty, dirtyCreation, showNotification, props, scroll]);

  return (
    <RainBonusModal
      {...props}
      scrollContainerRef={scrollContainerRef}
      dirtyRainRef={dirtyRainRef}
      toggleDropdown={toggleDropdown}
      toggleOrder={toggleOrder}
      handleSearch={handleSearch}
      createRain={createRain}
      creationInitialValues={creationInitialValues}
      resolutionHeightModal={resolutionHeightModal}
      formRef={formRef}
      onClose={handleClose}
      disabledSearch={disabledSearch}
      isDisabledSearch={isDisabledSearch}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dropdownState: rainModalSelector(state).dropdownState,
  order: rainModalSelector(state).order,
  actualRegion: regionSelector(state).actualRegion,
  loading: rainModalSelector(state).loading,
  rainDirty: rainModalSelector(state).rainDirty,
  dirtyCreation: rainModalSelector(state).dirtyCreation,
  rains: rainModalSelector(state).rains,
  nextRain: regionSelector(state).nextRain,
  filter: rainModalSelector(state).filter,
  timeErrorCode: rainModalSelector(state).timeErrorCreation,
  validatingRainTime: rainModalSelector(state).validatingRainTime,
  blockedTab: rainModalSelector(state).blockedTab,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDropdownState: (state: RainDropDownState) => {
    dispatch(actions.setDropdownState(state));
  },
  setFilter: (filters: RainTimeFilter) => {
    dispatch(actions.setFilter(filters));
  },
  setOrder: (state: ToggleOrderType) => {
    dispatch(actions.setOrder(state));
  },
  getRains: (params: RainsRequestParamsType, regionId: string) => {
    dispatch(actions.getRainsFetch({ params, regionId }));
  },
  addRain: (params: RainCreationParamsType) => {
    dispatch(actions.addRainFetch(params));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  setRainDirty: (dirty: boolean) => {
    dispatch(actions.setRainDirty(dirty));
  },
  setDirtyCreation: (dirty: boolean) => {
    dispatch(actions.setDirtyCreation(dirty));
  },
  resetValidationError: () => {
    dispatch(actions.resetTimeErrorCreation());
  },
  setBlockedTab: (tab: RainDropDownState) => {
    dispatch(actions.setBlockedTab(tab));
  },
  resetBlockedTab: () => {
    dispatch(actions.resetBlockedTab());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(RainBonusModalContainer);
